import { createThunk, model, view } from 'mst-collection';
import { getParent, types } from 'mobx-state-tree';

import { CourseLessonModel, CourseLesson } from './CourseLessonModel';
import { Api, schemas } from '@api';
import { Course } from './CourseModel';
import { ExtendedListModel } from '@models/utils/ExtendedListModel';
import { Tutorial } from '@models/Tutorials/TutorialModel';
import { AnalyticsService } from '@services';

export class CourseLessonList extends ExtendedListModel(
  types.reference(CourseLessonModel),
  {
    totalVideosCount: 0,
    watchedVideosCount: 0,
  },
  {
    idAttribute: 'id',
    schema: schemas.LessonListSchema,
  },
) {
  private get _parent(): Course {
    return getParent<Course>(this, 1);
  }

  get progressPercent() {
    if (this.totalVideosCount === 0) {
      return 0;
    }

    return (this.watchedVideosCount / this.totalVideosCount) * 100;
  }

  get allTutorials() {
    return this.asArray
      .map((lesson) => lesson.videos)
      .flat() as Tutorial[];
  }

  @view
  nextTutorial(tutorialId: number) {
    const index = this.allTutorials.findIndex(
      (t) => t.id === tutorialId,
    );

    if (
      typeof index === 'number' &&
      index !== -1 &&
      this.allTutorials[index + 1]
    ) {
      return this.allTutorials[index + 1];
    } else {
      return null;
    }
  }

  @view
  previousTutorial(tutorialId: number) {
    const index = this.allTutorials.findIndex(
      (t) => t.id === tutorialId,
    );

    if (
      typeof index === 'number' &&
      index !== -1 &&
      this.allTutorials[index - 1]
    ) {
      return this.allTutorials[index - 1];
    } else {
      return null;
    }
  }

  get playlist() {
    return {
      nextItem: this.nextTutorial,
      previousItem: this.previousTutorial,
    };
  }

  private setTotalVideosCount(value: number) {
    this.totalVideosCount = value;
  }

  private setWatchedVideosCount(value: number) {
    this.watchedVideosCount = value;
  }

  fetch = createThunk(() => {
    return async function fetch(this: CourseLessonList) {
      const res = await Api.Courses.getLessonsByCourseId({
        id: this._parent.id,
      });

      this.set(res.data.lessons);
      this.setTotalVideosCount(res.data.totalVideosCount);
      this.setWatchedVideosCount(res.data.watchedVideosCount);
    };
  });

  markTutorialAsDone = createThunk((tutorialId: number) => {
    return async function markTutorialAsDone(this: CourseLessonList) {
      const res = await Api.Courses.markTutorialAsDone({
        courseId: this._parent.id,
        tutorialId,
      });

      const tutorial = this.allTutorials.find(
        ({ id }) => id === tutorialId,
      );

      tutorial?.userStats?.markAsWatched();

      this.setWatchedVideosCount(res.data.watchedVideosCount);

      AnalyticsService.trackCourseProgressUpdated({
        courseId: this._parent.id,
        courseTitle: this._parent.title,
        value: this.progressPercent,
      });
    };
  });

  @view
  getLessonByTutorialId(
    tutorialId: number,
  ): CourseLesson | undefined {
    return this.asArray.find((lesson) =>
      lesson.tutorials.some((tutorial) => tutorial.id === tutorialId),
    );
  }
}

export const CourseLessonModelList = model(CourseLessonList);
